import { MOCK_ACCOUNTS_EN, MOCK_CURRENCIES_EN, MOCK_RATES_EN } from './locales/en/mock-accounts.js';
import { MOCK_ACCOUNTS_EN_US, MOCK_CURRENCIES_EN_US, MOCK_RATES_EN_US } from './locales/en-us/mock-accounts.js';
import { MOCK_ACCOUNTS_JA, MOCK_CURRENCIES_JA, MOCK_RATES_JA } from './locales/ja/mock-accounts.js';
import { MOCK_ACCOUNTS_KO, MOCK_CURRENCIES_KO, MOCK_RATES_KO } from './locales/ko/mock-accounts.js';
import { MOCK_ACCOUNTS_ZH_CN, MOCK_CURRENCIES_ZH_CN, MOCK_RATES_ZH_CN } from './locales/zh-cn/mock-accounts.js';
import { MOCK_ACCOUNTS_ZH_TW, MOCK_CURRENCIES_ZH_TW, MOCK_RATES_ZH_TW } from './locales/zh-tw/mock-accounts.js';

/**
 * @typedef {Object} MockAccount
 * @property {string} id
 * @property {string} name
 * @property {string} accountType
 * @property {string} currency
 * @property {string} currencySymbol
 * @property {number} currencyDecimalPlaces
 * @property {number} balance
 * @property {number|null} principal
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} MockCurrency
 * @property {string} code
 * @property {string} name
 * @property {string} symbol
 * @property {number} decimalPlaces
 * @property {number} isBase
 * @property {number} isCustom
 * @property {number} rate
 * @property {number|null} manualRate
 */

/** @type {Record<string, Array<MockAccount>>} */
const ACCOUNTS = {
	en: MOCK_ACCOUNTS_EN,
	'en-US': MOCK_ACCOUNTS_EN_US,
	ja: MOCK_ACCOUNTS_JA,
	ko: MOCK_ACCOUNTS_KO,
	'zh-CN': MOCK_ACCOUNTS_ZH_CN,
	'zh-TW': MOCK_ACCOUNTS_ZH_TW,
};

/** @type {Record<string, Array<MockCurrency>>} */
const CURRENCIES = {
	en: MOCK_CURRENCIES_EN,
	'en-US': MOCK_CURRENCIES_EN_US,
	ja: MOCK_CURRENCIES_JA,
	ko: MOCK_CURRENCIES_KO,
	'zh-CN': MOCK_CURRENCIES_ZH_CN,
	'zh-TW': MOCK_CURRENCIES_ZH_TW,
};

/** @type {Record<string, Array<MockCurrency>>} */
const RATES = {
	en: MOCK_RATES_EN,
	'en-US': MOCK_RATES_EN_US,
	ja: MOCK_RATES_JA,
	ko: MOCK_RATES_KO,
	'zh-CN': MOCK_RATES_ZH_CN,
	'zh-TW': MOCK_RATES_ZH_TW,
};

/** @type {Record<string, string>} */
const BASE = {
	en: 'GBP',
	'en-US': 'USD',
	ja: 'JPY',
	ko: 'KRW',
	'zh-CN': 'CNY',
	'zh-TW': 'TWD',
};

/**
 * The English mockups show GBP accounts with British-flavoured names; the
 * US English mockups show USD accounts with US-flavoured names; the Japanese
 * mockups show JPY accounts with Japanese names; the Korean mockups show KRW
 * accounts with Korean names; the Chinese mockups mix domestic and regional
 * currencies — CNY/SGD/USD for zh-CN and TWD/HKD/USD for zh-TW — with
 * Chinese names.
 * @param {string} lang
 * @returns {Array<MockAccount>}
 */
export function getMockAccounts(lang) {
	return ACCOUNTS[lang] ?? MOCK_ACCOUNTS_EN;
}

/**
 * @param {string} lang
 * @returns {Array<MockCurrency>}
 */
export function getMockCurrencies(lang) {
	return CURRENCIES[lang] ?? MOCK_CURRENCIES_EN;
}

/**
 * @param {string} lang
 * @returns {Array<MockCurrency>}
 */
export function getMockRates(lang) {
	return RATES[lang] ?? MOCK_RATES_EN;
}

/**
 * @param {string} lang
 * @returns {string}
 */
export function getBaseCurrency(lang) {
	return BASE[lang] ?? 'GBP';
}
